import { createSlice } from "@reduxjs/toolkit";
import { normalizeSectionName } from "../../utils/nameNormalization";

const DASHBOARD_SECTION_NAME = 'Desalination Plant';
const NORMALIZED_DASHBOARD_SECTION_NAME = normalizeSectionName(DASHBOARD_SECTION_NAME);

const initialState = {
    selectedSession: null,
    sessionButtons: [],
    status: "idle",
    error: null,
};

const sessionSlice = createSlice({
    name: "session",
    initialState,
    reducers: {
        setSessionButtons: (state, action) => {
            const buttons = Array.isArray(action.payload) ? action.payload : [];
            state.sessionButtons = buttons.map((button) => ({
                ...button,
                is_dashboard: normalizeSectionName(button?.section_name) === NORMALIZED_DASHBOARD_SECTION_NAME,
            }));
        },
        setSelectedSession: (state, action) => {
            // Completely replaces the previous session with new data
            // No merging - ensures clean state transition
            // Add a helper flag for the special "Desalination Plant" section
            const session = action.payload || {};
            const isDashboardSection = normalizeSectionName(session.section_name) === NORMALIZED_DASHBOARD_SECTION_NAME;
            state.selectedSession = {
                ...session,
                isDesalination: isDashboardSection,
                is_dashboard: isDashboardSection,
            };
        },
        clearSession: (state) => {
            // Clears the selected session, ensuring no residual data
            state.selectedSession = null;
        },
        resetSessionState: () => {
            // Resets entire session state to initial values (used on logout)
            return initialState;
        },
    },
});

export const {
    setSessionButtons,
    setSelectedSession,
    clearSession,
    resetSessionState
} = sessionSlice.actions;

// Selectors with JSON parsing support
export const selectSelectedSession = (state) => {
    const session = state.session?.selectedSession;
    // If it's a string, parse it; otherwise return as is
    if (typeof session === 'string') {
        try {
            return JSON.parse(session);
        } catch (e) {
            console.error('Error parsing selectedSession:', e);
            return null;
        }
    }
    return session;
};

export const selectSessionButtons = (state) => {
    const buttons = state.session?.sessionButtons;
    // If it's a string, parse it; otherwise return as is
    if (typeof buttons === 'string') {
        try {
            return JSON.parse(buttons);
        } catch (e) {
            console.error('Error parsing sessionButtons:', e);
            return [];
        }
    }
    return buttons;
};

export const selectCompanyId = (state) => {
    const session = selectSelectedSession(state);
    return session?.company_id;
};

export const selectCompanyName = (state) => {
    const session = selectSelectedSession(state);
    return session?.company_name;
};

export const selectLocationId = (state) => {
    const session = selectSelectedSession(state);
    return session?.location_id;
};

export const selectLocationName = (state) => {
    const session = selectSelectedSession(state);
    return session?.location_name;
};

export const selectSectionId = (state) => {
    const session = selectSelectedSession(state);
    return session?.section_id;
};

export const selectSectionName = (state) => {
    const session = selectSelectedSession(state);
    return session?.section_name;
};

export const selectSubSectionId = (state) => {
    const session = selectSelectedSession(state);
    return session?.sub_section_id;
};

export const selectSubSectionName = (state) => {
    const session = selectSelectedSession(state);
    return session?.sub_section_name;
};

export const selectDepartmentId = (state) => {
    const session = selectSelectedSession(state);
    return session?.department_id;
};

export const selectDepartmentName = (state) => {
    const session = selectSelectedSession(state);
    return session?.department_name;
};

// new selector for ps_id (project/session identifier)
export const selectPsId = (state) => {
    const session = selectSelectedSession(state);
    return session?.ps_id;
};

// selector to check if current session is the special desalination one
export const selectIsDesalination = (state) => {
    const session = selectSelectedSession(state);
    return !!session?.isDesalination;
};

// selector to check if current session should open dashboard view
export const selectIsDashboard = (state) => {
    const session = selectSelectedSession(state);
    return !!session?.is_dashboard;
};

export default sessionSlice.reducer;